import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { resubscribeEmail, unsubscribeEmail } from '@/api/auth'
import type { ApiError } from '@/api/client'

/**
 * One-click email unsubscribe landing page. Reads {@code ?token=...} from the footer link of a
 * commercial email and opts the contact out (do_not_email) — AU SPAM Act 2003. The token is a
 * signed HMAC of agency+contact, so no session is required. Idempotent: re-visiting is a no-op.
 *
 * After unsubscribing it offers a self-service Re-subscribe (same token) for anyone who clicked by
 * mistake or wants back in — their click is the renewed consent.
 */
export default function Unsubscribe() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const unsubscribe = useMutation({ mutationFn: (t: string) => unsubscribeEmail(t) })
  const resubscribe = useMutation({ mutationFn: (t: string) => resubscribeEmail(t) })

  // Fire once on mount — the click on the email link IS the opt-out action.
  const fired = useRef(false)
  useEffect(() => {
    if (fired.current) return
    fired.current = true
    if (token) unsubscribe.mutate(token)
  }, [token, unsubscribe])

  const error = (resubscribe.error ?? unsubscribe.error) as ApiError | null

  let body
  if (!token) {
    body = <p className="text-sm text-danger">This unsubscribe link is missing its token.</p>
  } else if (resubscribe.isSuccess) {
    body = (
      <p className="text-sm text-gray-500">
        You're subscribed again. You'll receive emails from this agency.
      </p>
    )
  } else if (unsubscribe.isSuccess) {
    body = (
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          You've been unsubscribed. You won't receive any more marketing emails from this agency.
        </p>
        <div>
          <p className="text-xs text-gray-400 mb-2">Changed your mind?</p>
          <button
            type="button"
            onClick={() => resubscribe.mutate(token)}
            disabled={resubscribe.isPending}
            className="px-4 py-2 rounded-lg bg-brand-primary text-white text-sm font-medium hover:bg-brand-dark disabled:opacity-60"
          >
            {resubscribe.isPending ? 'Re-subscribing…' : 'Re-subscribe'}
          </button>
        </div>
      </div>
    )
  } else if (error) {
    body = <p className="text-sm text-danger">{error.message}</p>
  } else {
    body = <p className="text-sm text-gray-500">Processing your request…</p>
  }

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-10 w-full max-w-sm text-center">
        <img src="/brand/profitpipe-wordmark.svg" alt="ProfitPipe" className="h-5 mx-auto mb-5" />
        <h1 className="text-2xl font-bold text-brand-darkest mb-4">Unsubscribe</h1>
        {body}
      </div>
    </div>
  )
}
